/**
 * The rules of an encounter being recorded (PRD-0032 SC-018, slice 05), pure: what a
 * detention alone may carry, when the GC III Art. 17 card applies, what still blocks saving,
 * and the local-time form of the capture time. The forms apply every change through
 * `patchEncounter`, so a field the new type no longer allows is dropped, never sent.
 */
import type { Gc3Particulars } from '../check/check-types'
import type { EncounterDraft } from './encounter-types'

export const EMPTY_GC3: Gc3Particulars = {
  nameAsGiven: '',
  rank: '',
  dateOfBirth: '',
  serviceNumber: '',
}

/**
 * The GC III Art. 17 card (name as given, rank, date of birth, service number) applies to a
 * detention, or to anyone recorded as a prisoner of war.
 */
export function showsArt17(draft: Pick<EncounterDraft, 'type' | 'legalStatus'>): boolean {
  return draft.type === 'detention' || draft.legalStatus === 'prisoner_of_war'
}

/**
 * Merge a change into the draft and drop what no longer applies: the capture tag and legal
 * status belong to a detention only, the Art. 17 card to a detention or a prisoner of war.
 * Typing the unit takes the "prefilled" mark off; editing the time, the "from the phone" one.
 */
export function patchEncounter(
  draft: EncounterDraft,
  patch: Partial<EncounterDraft>
): EncounterDraft {
  const next: EncounterDraft = { ...draft, ...patch }
  if (next.type !== 'detention') {
    next.captureTag = null
    next.legalStatus = null
  }
  if (!showsArt17(next)) next.gc3 = null
  else if (!next.gc3) next.gc3 = EMPTY_GC3
  if (patch.unit !== undefined && patch.unitPrefilled === undefined) next.unitPrefilled = false
  if (patch.at !== undefined && patch.atIsAuto === undefined) next.atIsAuto = false
  return next
}

/** What still blocks saving the encounter. */
export type EncounterGap =
  /** A type that cannot be recorded yet (a source meeting). */
  | 'type'
  /** The capture time is not a time, or lies in the future. */
  | 'time'
  /** A place was typed that is neither MGRS nor coordinates. */
  | 'location'
  /** The encounter to join is not decided yet: the recent encounters are still loading. */
  | 'choice'

/** A capture time up to this far ahead of the phone's clock is still "now". */
const FUTURE_SLACK_MS = 5 * 60_000

/** A typed place that did not parse: text in the manual field, but no typed position. */
export function typedPlaceInvalid(draft: EncounterDraft): boolean {
  if (draft.locationMode !== 'manual' || !draft.manualGrid?.trim()) return false
  return draft.location?.source !== 'manual'
}

export function encounterGaps(draft: EncounterDraft, now: Date): EncounterGap[] {
  const gaps: EncounterGap[] = []
  if (draft.type === 'source_meeting') gaps.push('type')
  const at = Date.parse(draft.at)
  if (Number.isNaN(at) || at > now.getTime() + FUTURE_SLACK_MS) gaps.push('time')
  if (typedPlaceInvalid(draft)) gaps.push('location')
  return gaps
}

export { fromLocalInputValue, toLocalInputValue, toLocalIso } from '@/utils/date'
